import { SelectOption } from 'global';

import { CustomerVisibility } from '../enums';
import { DealSheetFormValues } from '../ui/rule/types';
import { DealSheetRulesTableRow } from '../ui/table/types';

class DealSheetConfigurationMapper {
  processSalesCenterFormToAPIDto(formData: DealSheetFormValues): Record<string, any> {
    const charges = formData.charges.map((item, index) => this.chargeToAPIDto(item, index));
    const taxes = formData.taxes.map((item, index) => this.taxToAPIDto(item, formData.charges.length + index));

    return {
      charges: [...charges, ...taxes],

      name: formData.name,
      ...(formData.description ? { description: formData.description } : {}),

      dealRules: {
        ...([
          ...formData.equipment.type,
          ...formData.equipment.model,
          ...formData.equipment.make,
          ...formData.equipment.year,
        ].length
          ? {
              equipment: {
                ...(formData.equipment.type.length ? { type: formData.equipment.type } : {}),
                ...(formData.equipment.model.length ? { model: formData.equipment.model } : {}),
                ...(formData.equipment.make.length ? { make: formData.equipment.make } : {}),
                ...(formData.equipment.year.length ? { year: formData.equipment.year } : {}),
              },
            }
          : {}),

        ...(Object.values(formData.customer).some(value => !!value.length)
          ? {
              customer: {
                ...(formData?.customer?.type?.length
                  ? {
                      type: formData.customer.type,
                    }
                  : {}),
                ...(formData?.customer?.paymentType?.length
                  ? {
                      paymentType: formData.customer.paymentType,
                    }
                  : {}),
              },
            }
          : {}),

        ...(Object.values(formData?.address)
          .filter(value => Array.isArray(value))
          .some(value => !!value.length)
          ? {
              location: {
                country: formData.address.country,

                ...(formData?.address?.zip?.length
                  ? {
                      zip: formData.address.zip,
                    }
                  : {}),

                ...(formData?.address?.city?.length
                  ? {
                      city: formData.address.city,
                    }
                  : {}),

                ...(formData?.address?.postalCode?.length
                  ? {
                      postalCode: formData.address.postalCode,
                    }
                  : {}),

                ...(formData?.address?.province?.length
                  ? {
                      province: formData.address.province,
                    }
                  : {}),

                ...(formData?.address?.county?.length
                  ? {
                      county: formData.address.county,
                    }
                  : {}),

                ...(formData?.address?.state?.length
                  ? {
                      state: formData.address.state,
                    }
                  : {}),
              },
            }
          : {}),
      },
    };
  }

  private chargeToAPIDto(charge: DealSheetFormValues['charges'][number], sortOrder: number) {
    return {
      sortOrder,
      id: charge.id,
      cap: charge.cap,
      equipmentId: null,
      name: charge.name,
      type: charge.type,
      amount: charge.amount,
      parentId: charge.parentId,
      // DR-10718: internal-only fees are never taxed, so saving clears a Taxable flag left by older rules
      isTaxable: charge.isTaxable && charge.customerVisibility !== CustomerVisibility.InternalOnly,
      amountType: charge.amountType,
      description: charge.description,
      isEquipmentLvl: charge.isEquipmentLvl,
      customerVisibility: charge.customerVisibility,
    };
  }

  private taxToAPIDto(tax: DealSheetFormValues['taxes'][number], sortOrder: number) {
    return {
      sortOrder,
      id: tax.id,
      cap: tax.cap,
      name: tax.name,
      type: tax.type,
      equipmentId: null,
      amount: tax.amount,
      parentId: tax.parentId,
      amountType: tax.amountType,
      description: tax.description,
      isEquipmentLvl: tax.isEquipmentLvl,
      customerVisibility: tax.customerVisibility,
    };
  }

  public processDealSheetToTable(
    tableData: Record<string, any>[],
    {
      countries,
      usRegions,
      canadaRegions,
    }: {
      usRegions: SelectOption[];
      countries: SelectOption[];
      canadaRegions: SelectOption[];
    }
  ): DealSheetRulesTableRow[] {
    return tableData.map(rule => ({
      id: rule.id,
      name: rule.name,
      description: rule.description,
      createdDateTime: rule.createdDateTime,

      propCustomerType: rule.dealRules.customer?.type,
      propCustomerPaymentType: rule.dealRules.customer?.paymentType,

      propLocationZip: rule.dealRules.location?.zip,
      propLocationCity: rule.dealRules.location?.city,
      propLocationCounty: rule.dealRules.location?.county,
      propLocationPostalCode: rule.dealRules.location?.postalCode,
      propLocationState: rule.dealRules.location?.state?.map(
        (stateValue: any) => usRegions.find(({ value }) => value === stateValue)?.label ?? stateValue
      ),
      propLocationCountry: rule.dealRules.location?.country?.map(
        (countryValue: any) => countries.find(({ value }) => value === countryValue)?.label ?? countryValue
      ),
      propLocationProvince: rule.dealRules.location?.province?.map(
        (provinceValue: any) => canadaRegions.find(({ value }) => value === provinceValue)?.label ?? provinceValue
      ),

      propEquipmentType: rule.dealRules.equipment?.type,
      propEquipmentYear: rule.dealRules.equipment?.year,
      propEquipmentMake: rule.dealRules.equipment?.make,
      propEquipmentModel: rule.dealRules.equipment?.model,
    }));
  }
}

export const dealSheetConfigurationMapper = new DealSheetConfigurationMapper();
